import { Link, useNavigate } from 'react-router-dom';
import useQueryValues from '@/hooks/useQueryValues';
import usePaginationQuery from '@/hooks/usePaginationQuery';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useQuery } from '@tanstack/react-query';
import MainContentTitle from '@/components/molecule/MainContentTitle';
import { NAV_CONST } from '@/src/constants/nav';
import Filter from '@/components/organism/Filter';
import SectionCardNoneTitle from '@/components/molecule/SectionCardNoneTitle';
import Typo from '@/components/atom/Typo';
import PATH from '@/src/routes/path';
import Button from '@/components/atom/Button';
import LucideIcon from '@/components/atom/Icons/LucideIcon';
import Table from '@/components/atom/Table';
import {
  getPushAlarmCategoryAPI,
  getPushAlarmInfosAPI,
} from '@/src/apis/rest/pushAlarm';
import { pushAlarmInfoListColumn } from '@/src/pages/pushAlarm/column';
import { pushAlarmInfoListFilters } from '@/src/pages/pushAlarm/filter';

function PushAlarmListPage() {
  const navigate = useNavigate();

  const { defaultValues: defaultSearchParams } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();

  const gridRef = useRef<AgGridReact>(null);
  const [searchParams, setSearchParams] = useState(defaultSearchParams);

  const {
    data: pushAlarmInfoListData,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['push-alarm', searchParams, page, pageSize],
    queryFn: () => {
      return getPushAlarmInfosAPI({
        ...searchParams,
        page,
        pageSize,
      });
    },
  });

  const { data: pushCategoryTypeData } = useQuery({
    queryKey: ['push-alarm-category'],
    staleTime: Infinity,
    queryFn: () => {
      return getPushAlarmCategoryAPI();
    },
  });

  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  const categoryOptions = useMemo(() => {
    const categoryMap =
      pushCategoryTypeData?.map(e => ({
        label: e.value,
        value: e.key,
      })) || [];
    return [{ label: '전체', value: '' }, ...categoryMap];
  }, [pushCategoryTypeData]);

  return (
    <>
      <MainContentTitle
        pageTitle={NAV_CONST.pushAlarmInfoManagement}
        breadcrumbList={[NAV_CONST.pushAlarmInfoManagement]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={pushAlarmInfoListFilters(categoryOptions)}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setSearchParams(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">푸시 알림 목록</Typo>
              <Link to={PATH.PUSH_ALARM_CREATE}>
                <Button buttonSize="medium" onClick={() => {}}>
                  <div className="flex items-center gap-x-2">
                    <LucideIcon name="Plus" color="white" />
                    푸시 등록
                  </div>
                </Button>
              </Link>
            </div>

            <Table
              ref={gridRef}
              rowData={isError ? [] : pushAlarmInfoListData?.rows || []}
              isPaginationDataMapping
              columnDefs={pushAlarmInfoListColumn(e => {
                navigate(PATH.PUSH_ALARM_DETAIL.replace(':id', String(e)));
              })}
              total={pushAlarmInfoListData?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>
    </>
  );
}

export default PushAlarmListPage;
